import { AtlasChatCaseContextData } from '@secca/shared/models/atlas/atlas-chat.model';
import { toNonEmptyString } from '@secca/shared/helpers/string-helper';

export function compactAtlasCaseContextData(data: AtlasChatCaseContextData): AtlasChatCaseContextData | null {
  const compacted = Object.entries(data).reduce((result, [key, value]) => {
    if (Array.isArray(value) && !value.length) {
      return result;
    }

    if (value !== null && value !== undefined && value !== '') {
      result[key as keyof AtlasChatCaseContextData] = value;
    }

    return result;
  }, {} as AtlasChatCaseContextData);

  return Object.keys(compacted).length ? compacted : null;
}

export function formatWinterSportEnabled(value: boolean | null | undefined): string | null {
  if (value == null) {
    return null;
  }

  return value ? 'Enabled' : 'Disabled';
}

export function formatIncidentLocation(location: any): string | null {
  if (!location) {
    return null;
  }

  const freeText = location?.freeText?.trim();
  if (freeText) {
    return freeText;
  }

  const parts = [
    location?.locality?.trim(),
    location?.administrativeAreaLevel1?.trim(),
    location?.country?.trim()
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(', ') : null;
}

export function formatIncidentCauseLevel(incident: any, level: 1 | 2 | 3): string | null {
  const text = toNonEmptyString(incident?.[`causeLevel${level}Text`]);
  const code = toNonEmptyString(incident?.[`causeLevel${level}Code`]);

  if (text && code) {
    return `${text} (${code})`;
  }

  return text || code;
}

export function serializeCaseValidationLocation(location: any): string | null {
  if (!location) {
    return null;
  }

  const freeText = toNonEmptyString(location?.freeText);
  if (freeText) {
    return freeText;
  }

  return toNonEmptyString(location?.locality) ||
    toNonEmptyString(location?.administrativeAreaLevel1) ||
    toNonEmptyString(location?.country);
}

export function serializePurposesOfTravelForAtlas(value: any): string[] | undefined {
  if (!Array.isArray(value) || !value.length) {
    return undefined;
  }

  const purposes = value
    .map(item => toNonEmptyString(item))
    .filter(Boolean) as string[];

  return purposes.length ? purposes : undefined;
}

export function getMainDiagnosisText(incident: any): string | null {
  const mainDiagnosis = getMainDiagnosis(incident);
  if (!mainDiagnosis) {
    return null;
  }

  const diagnosisName = toNonEmptyString(mainDiagnosis?.shortName);
  const icd10Code = toNonEmptyString(mainDiagnosis?.icd10Code);

  if (diagnosisName && icd10Code) {
    return `${diagnosisName} (${icd10Code})`;
  }

  return diagnosisName || icd10Code || null;
}

export function getMainDiagnosis(incident: any): any | null {
  return getMainDiagnosisWithSource(incident)?.diagnosis || null;
}

export function getMainDiagnosisWithSource(incident: any): { diagnosis: any; source: string } | null {
  return getDiagnosisSources(incident).find(item =>
    toNonEmptyString(item.diagnosis?.icd10Code) ||
    toNonEmptyString(item.diagnosis?.shortName)
  ) || null;
}

export function getDiagnosisSources(incident: any): Array<{ diagnosis: any; source: string }> {
  return [
    { diagnosis: incident?.medicalPreAssessmentDiagnosis, source: 'Medical pre-assessment diagnosis' },
    { diagnosis: incident?.medicalDiagnosis, source: 'Medical advised diagnosis' },
    { diagnosis: incident?.diagnosis, source: 'General main diagnosis' }
  ];
}
